/**
 * WHO MAY SEE A ROUTE, AS A PURE FUNCTION OF THE SESSION.
 *
 * ONLY A LIVE BUILD IS GATED. `./SessionGate.tsx` returns its children untouched unless
 * `isLiveApiConfigured()` is true, because the default build is fixtures with no session at all
 * and the accessibility leg checks exactly that build. This file is what a LIVE build asks.
 *
 * THE RULE IS THE CORPUS'S THREE GATES, READ OFF `GET /v1/me` AND NOTHING ELSE: the application
 * endpoints "accept any signed-in Firebase user", the editor needs the author capability, and "the
 * cabinet is reachable by a disabled author" (`docs/design/07-api/authors-api.yaml`). A
 * route asks for one of two things:
 *
 *   signed-in   the application flow and the support pages - "authenticated but not
 *               capability-gated" (`routes/apply/index.tsx`);
 *   cabinet     every cabinet screen - `cabinetReachable`: ANY author row since design 25, so an
 *               applicant's, and a DISABLED author's, who "reaches the cabinet, sees their disabled
 *               state, and has payout actions refused" (`authors-api.yaml`).
 *
 * AND ONE DETOUR ON THE WAY INTO A CABINET SCREEN: THE TERMS INTERSTITIAL. An author whose
 * acceptance is stale is sent to `/terms?next=<that screen>` - once per visit, and only an author
 * with the capability the terms gate (`termsInterstitialDue` in ./session-state.ts argues each
 * condition). It is never a refusal: once it has been shown, every cabinet screen renders again,
 * stale acceptance or not, because reading the cabinet is not what a stale acceptance blocks.
 *
 * TWO RULES FROM ELSEWHERE THAT THIS FUNCTION MUST NOT BREAK:
 *
 *   1. A 503 NEVER ENDS A SESSION (adr/0010-firebase-integration.md). `reconnecting` with a
 *      session already loaded RENDERS - "Kept through `reconnecting` so the cabinet keeps
 *      rendering" (`./session-state.ts`). Only a person who never had a session loaded is sent
 *      to `/sign-in`, which renders the countdown itself rather than a sign-in form.
 *   2. G30: an anonymous session must not reach the application flow, and the remedy is a LINK
 *      that keeps the uid (`./session-state.ts`). The link prompt lives on `/sign-in`, so
 *      an anonymous session is sent there from every gated route, the application form included.
 */
import { cabinetIsReachable, needsProviderLink, termsInterstitialDue } from './session-state';
import type { SessionState } from './session-state';

export type GateRequirement = 'signed-in' | 'cabinet';

export type GateDecision =
  /** The session answers the route's question. Render it. */
  | { readonly kind: 'render' }
  /** Nothing is known yet - the SDK has not reported, or `GET /v1/me` is in flight. */
  | { readonly kind: 'wait' }
  /** To `/sign-in`, carrying this address back. Signed out, refused, or owed a G30 link. */
  | { readonly kind: 'sign-in' }
  /** Signed in and loaded, and not their surface: a cabinet route, and no author row yet. */
  | { readonly kind: 'not-reachable' }
  /** To `/terms`, carrying this address back: the interstitial, the first time it is due. */
  | { readonly kind: 'terms' };

function onLoadedSession(state: SessionState, requirement: GateRequirement): GateDecision {
  if (needsProviderLink(state)) {
    return { kind: 'sign-in' };
  }
  if (requirement === 'cabinet' && !cabinetIsReachable(state)) {
    return { kind: 'not-reachable' };
  }
  if (requirement === 'cabinet' && termsInterstitialDue(state)) {
    return { kind: 'terms' };
  }
  return { kind: 'render' };
}

export function gateDecision(state: SessionState, requirement: GateRequirement): GateDecision {
  switch (state.phase) {
    case 'booting':
    case 'signing-in':
      return { kind: 'wait' };

    case 'signed-out':
    case 'refused':
      return { kind: 'sign-in' };

    case 'loading-session':
      // A re-read over a session already on screen is not a reason to blank it.
      return state.session === null ? { kind: 'wait' } : onLoadedSession(state, requirement);

    case 'reconnecting':
      // THE SESSION IS HELD - rule 1 above. With nothing loaded there is nothing to hold, and
      // `/sign-in` is the screen that renders the countdown and the retry.
      return state.session === null ? { kind: 'sign-in' } : onLoadedSession(state, requirement);

    case 'ready':
      return onLoadedSession(state, requirement);
  }
}
